import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  FileText, 
  Download, 
  RefreshCw, 
  Filter,
  Clock,
  AlertCircle,
  CheckCircle,
  XCircle,
  Activity
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const APILogsViewer = ({ tenantId }) => {
  const [logs, setLogs] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [auditTotalCount, setAuditTotalCount] = useState(0);
  const [autoRefresh, setAutoRefresh] = useState(false);
  
  // Filters for API logs
  const [filters, setFilters] = useState({
    api_key_id: '',
    status_code: '',
    start_date: '',
    end_date: '',
    limit: 100,
    offset: 0
  });
  
  // Filters for audit logs
  const [auditFilters, setAuditFilters] = useState({
    action_type: '',
    start_date: '',
    end_date: '',
    limit: 100,
    offset: 0
  });

  useEffect(() => {
    if (tenantId) {
      fetchAPIKeys();
      fetchLogs();
      fetchAuditLogs();
    }
  }, [tenantId]);

  // Auto-refresh every 30 seconds when enabled
  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(() => {
        fetchLogs();
        fetchAuditLogs();
      }, 30000);
      return () => clearInterval(interval);
    }
  }, [autoRefresh, filters, auditFilters]);

  const fetchAPIKeys = async () => {
    try {
      const response = await axios.get(`${API}/saas-admin/tenants/${tenantId}/api-keys`, {
        withCredentials: true
      });
      setApiKeys(response.data.api_keys || []);
    } catch (error) {
      console.error('Failed to load API keys:', error);
    }
  };

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.api_key_id) params.append('api_key_id', filters.api_key_id);
      if (filters.status_code) params.append('status_code', filters.status_code);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      params.append('limit', filters.limit);
      params.append('offset', filters.offset);

      const response = await axios.get(`${API}/saas-admin/tenants/${tenantId}/api-logs?${params}`, {
        withCredentials: true
      });
      
      setLogs(response.data.logs || []);
      setTotalCount(response.data.total_count || 0);
    } catch (error) {
      toast.error('Failed to load API logs');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [tenantId, filters]);

  const fetchAuditLogs = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      params.append('tenant_id', tenantId);
      if (auditFilters.action_type) params.append('action_type', auditFilters.action_type);
      if (auditFilters.start_date) params.append('start_date', auditFilters.start_date);
      if (auditFilters.end_date) params.append('end_date', auditFilters.end_date);
      params.append('limit', auditFilters.limit);
      params.append('offset', auditFilters.offset);

      const response = await axios.get(`${API}/saas-admin/audit-logs?${params}`, {
        withCredentials: true
      });
      
      setAuditLogs(response.data.logs || []);
      setAuditTotalCount(response.data.total_count || 0);
    } catch (error) {
      toast.error('Failed to load audit logs');
      console.error(error);
    }
  }, [tenantId, auditFilters]);

  const handleExportCSV = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.api_key_id) params.append('api_key_id', filters.api_key_id);
      if (filters.status_code) params.append('status_code', filters.status_code);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);

      const response = await axios.get(
        `${API}/saas-admin/tenants/${tenantId}/api-logs/export?${params}`,
        {
          withCredentials: true,
          responseType: 'blob'
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `api_logs_${tenantId}_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('CSV exported successfully');
    } catch (error) {
      toast.error('Failed to export CSV');
      console.error(error);
    }
  };

  const getStatusBadge = (statusCode) => {
    if (statusCode >= 200 && statusCode < 300) {
      return <Badge className="bg-green-100 text-green-700"><CheckCircle className="w-3 h-3 mr-1" />{statusCode}</Badge>;
    } else if (statusCode >= 400 && statusCode < 500) {
      return <Badge className="bg-yellow-100 text-yellow-700"><AlertCircle className="w-3 h-3 mr-1" />{statusCode}</Badge>;
    } else if (statusCode >= 500) {
      return <Badge className="bg-red-100 text-red-700"><XCircle className="w-3 h-3 mr-1" />{statusCode}</Badge>;
    }
    return <Badge>{statusCode}</Badge>;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">API Logs & Audit Trail</h2>
          <p className="text-sm text-slate-600 mt-1">Monitor API activity and SuperAdmin actions</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              fetchLogs();
              fetchAuditLogs();
            }}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            variant={autoRefresh ? "default" : "outline"}
            size="sm"
            onClick={() => setAutoRefresh(!autoRefresh)}
          >
            <Activity className="w-4 h-4 mr-2" />
            {autoRefresh ? 'Auto-Refresh ON' : 'Auto-Refresh OFF'}
          </Button>
        </div>
      </div>

      <Tabs defaultValue="api-logs" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="api-logs">
            <FileText className="w-4 h-4 mr-2" />
            API Request Logs ({totalCount})
          </TabsTrigger>
          <TabsTrigger value="audit-logs">
            <Activity className="w-4 h-4 mr-2" />
            Audit Trail ({auditTotalCount})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="api-logs" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Filter className="w-5 h-5" />
                Filters
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <Label>API Key</Label>
                  <Select
                    value={filters.api_key_id}
                    onValueChange={(value) => setFilters({ ...filters, api_key_id: value, offset: 0 })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="All Keys" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">All Keys</SelectItem>
                      {apiKeys.map(key => (
                        <SelectItem key={key.id} value={key.id}>{key.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Status Code</Label>
                  <Select
                    value={filters.status_code}
                    onValueChange={(value) => setFilters({ ...filters, status_code: value, offset: 0 })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="All Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">All Status</SelectItem>
                      <SelectItem value="200">200 (Success)</SelectItem>
                      <SelectItem value="201">201 (Created)</SelectItem>
                      <SelectItem value="401">401 (Unauthorized)</SelectItem>
                      <SelectItem value="403">403 (Forbidden)</SelectItem>
                      <SelectItem value="422">422 (Validation Error)</SelectItem>
                      <SelectItem value="429">429 (Rate Limited)</SelectItem>
                      <SelectItem value="500">500 (Server Error)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Start Date</Label>
                  <Input
                    type="datetime-local"
                    value={filters.start_date}
                    onChange={(e) => setFilters({ ...filters, start_date: e.target.value ? new Date(e.target.value).toISOString() : '', offset: 0 })}
                  />
                </div>

                <div>
                  <Label>End Date</Label>
                  <Input
                    type="datetime-local"
                    value={filters.end_date}
                    onChange={(e) => setFilters({ ...filters, end_date: e.target.value ? new Date(e.target.value).toISOString() : '', offset: 0 })}
                  />
                </div>
              </div>

              <div className="flex gap-2">
                <Button onClick={fetchLogs} disabled={loading}>
                  Apply Filters
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setFilters({
                      api_key_id: '',
                      status_code: '',
                      start_date: '',
                      end_date: '',
                      limit: 100,
                      offset: 0
                    });
                  }}
                >
                  Clear
                </Button>
                <Button variant="outline" onClick={handleExportCSV} className="ml-auto">
                  <Download className="w-4 h-4 mr-2" />
                  Export CSV
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Recent API Requests</CardTitle>
              <CardDescription>
                Showing {logs.length} of {totalCount} total requests
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Timestamp</TableHead>
                      <TableHead>API Key</TableHead>
                      <TableHead>Endpoint</TableHead>
                      <TableHead>Method</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>IP Address</TableHead>
                      <TableHead>Response Time</TableHead>
                      <TableHead>Error</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {logs.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center text-slate-500 py-8">
                          No API logs found
                        </TableCell>
                      </TableRow>
                    ) : (
                      logs.map((log) => (
                        <TableRow key={log.id}>
                          <TableCell className="text-sm">
                            <div className="flex items-center gap-1">
                              <Clock className="w-3 h-3 text-slate-400" />
                              {formatDate(log.created_at)}
                            </div>
                          </TableCell>
                          <TableCell className="text-sm">
                            {log.metadata?.api_key_name || log.api_key_id?.substring(0, 8) || 'N/A'}
                          </TableCell>
                          <TableCell className="text-sm font-mono">{log.endpoint}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{log.method}</Badge>
                          </TableCell>
                          <TableCell>{getStatusBadge(log.status_code)}</TableCell>
                          <TableCell className="text-sm font-mono">{log.ip_address}</TableCell>
                          <TableCell className="text-sm">
                            {log.response_time_ms ? `${Math.round(log.response_time_ms)}ms` : 'N/A'}
                          </TableCell>
                          <TableCell className="text-sm">
                            {log.error_code ? (
                              <span className="text-red-600">{log.error_code}</span>
                            ) : (
                              '-'
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              {totalCount > filters.limit && (
                <div className="flex items-center justify-between mt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={filters.offset === 0}
                    onClick={() => setFilters({ ...filters, offset: Math.max(0, filters.offset - filters.limit) })}
                  >
                    Previous
                  </Button>
                  <span className="text-sm text-slate-600">
                    {filters.offset + 1} - {Math.min(filters.offset + filters.limit, totalCount)} of {totalCount}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={filters.offset + filters.limit >= totalCount}
                    onClick={() => setFilters({ ...filters, offset: filters.offset + filters.limit })}
                  >
                    Next
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="audit-logs" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Filter className="w-5 h-5" />
                Filters
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label>Action Type</Label>
                  <Select
                    value={auditFilters.action_type}
                    onValueChange={(value) => setAuditFilters({ ...auditFilters, action_type: value, offset: 0 })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="All Actions" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">All Actions</SelectItem>
                      <SelectItem value="api_key_created">API Key Created</SelectItem>
                      <SelectItem value="api_key_revoked">API Key Revoked</SelectItem>
                      <SelectItem value="api_key_rotated">API Key Rotated</SelectItem>
                      <SelectItem value="api_access_toggled">API Access Toggled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Start Date</Label>
                  <Input
                    type="datetime-local"
                    value={auditFilters.start_date}
                    onChange={(e) => setAuditFilters({ ...auditFilters, start_date: e.target.value ? new Date(e.target.value).toISOString() : '', offset: 0 })}
                  />
                </div>

                <div>
                  <Label>End Date</Label>
                  <Input
                    type="datetime-local"
                    value={auditFilters.end_date}
                    onChange={(e) => setAuditFilters({ ...auditFilters, end_date: e.target.value ? new Date(e.target.value).toISOString() : '', offset: 0 })}
                  />
                </div>
              </div>

              <div className="flex gap-2">
                <Button onClick={fetchAuditLogs}>
                  Apply Filters
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setAuditFilters({
                      action_type: '',
                      start_date: '',
                      end_date: '',
                      limit: 100,
                      offset: 0
                    });
                  }}
                >
                  Clear
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>SuperAdmin Actions</CardTitle>
              <CardDescription>
                Showing {auditLogs.length} of {auditTotalCount} total actions
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Timestamp</TableHead>
                      <TableHead>Admin</TableHead>
                      <TableHead>Action</TableHead>
                      <TableHead>Resource</TableHead>
                      <TableHead>Details</TableHead>
                      <TableHead>IP Address</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {auditLogs.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-slate-500 py-8">
                          No audit logs found
                        </TableCell>
                      </TableRow>
                    ) : (
                      auditLogs.map((log) => (
                        <TableRow key={log.id}>
                          <TableCell className="text-sm">
                            <div className="flex items-center gap-1">
                              <Clock className="w-3 h-3 text-slate-400" />
                              {formatDate(log.created_at)}
                            </div>
                          </TableCell>
                          <TableCell className="text-sm">
                            <div>
                              <div className="font-medium">{log.admin_email}</div>
                              <div className="text-xs text-slate-500">{log.admin_user_id.substring(0, 8)}</div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge>{log.action_type}</Badge>
                          </TableCell>
                          <TableCell className="text-sm">
                            <div>
                              <div className="font-medium">{log.resource_type}</div>
                              {log.resource_id && (
                                <div className="text-xs text-slate-500 font-mono">{log.resource_id.substring(0, 12)}</div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-sm max-w-xs">
                            <div className="truncate" title={JSON.stringify(log.details)}>
                              {log.details?.key_name || log.details?.tenant_name || JSON.stringify(log.details).substring(0, 50)}
                            </div>
                          </TableCell>
                          <TableCell className="text-sm font-mono">{log.ip_address || 'N/A'}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              {auditTotalCount > auditFilters.limit && (
                <div className="flex items-center justify-between mt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={auditFilters.offset === 0}
                    onClick={() => setAuditFilters({ ...auditFilters, offset: Math.max(0, auditFilters.offset - auditFilters.limit) })}
                  >
                    Previous
                  </Button>
                  <span className="text-sm text-slate-600">
                    {auditFilters.offset + 1} - {Math.min(auditFilters.offset + auditFilters.limit, auditTotalCount)} of {auditTotalCount}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={auditFilters.offset + auditFilters.limit >= auditTotalCount}
                    onClick={() => setAuditFilters({ ...auditFilters, offset: auditFilters.offset + auditFilters.limit })}
                  >
                    Next
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default APILogsViewer;
